import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { API_CONFIG } from '../../config';
import { readApiResponse, SESSION_EXPIRED_MESSAGE } from '../../lib/utils/apiResponse';
import { isTokenExpired } from '../../lib/utils/jwt';

const API_URL = API_CONFIG.API_URL;

// Types
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  gender?: string;
  roles: string[];
  createdAt: string;
}

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  gender?: string;
}

interface AuthResponse {
  accessToken: string;
  accessTokenExpiration: string;
  user: User;
}

// Helper to get token from localStorage
const getStoredToken = (): string | null => {
  return localStorage.getItem('accessToken');
};

// Helper to get user from localStorage
const getStoredUser = (): User | null => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return null;
  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
};

/**
 * Restore the session from localStorage, but only if the token is still valid.
 * A token that expired while the tab was closed used to be restored as a live
 * session, so the UI looked signed in and every request failed until something
 * hit a 401. Checking `exp` up front means we start signed out instead.
 */
const restoreSession = (): Pick<AuthState, 'user' | 'accessToken' | 'isAuthenticated'> => {
  const token = getStoredToken();

  if (token && isTokenExpired(token)) {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    return { user: null, accessToken: null, isAuthenticated: false };
  }

  return {
    user: getStoredUser(),
    accessToken: token,
    isAuthenticated: !!token,
  };
};

// Initial state
const initialState: AuthState = {
  ...restoreSession(),
  isLoading: false,
  error: null,
};

// Async thunks
export const login = createAsyncThunk<AuthResponse, LoginCredentials>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });

      if (response.status === 429) {
        return rejectWithValue('Too many login attempts. Please wait a moment and try again.');
      }

      const data = await readApiResponse(response);

      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.[0] || 'Login failed');
      }

      // Store token and user in localStorage
      localStorage.setItem('accessToken', data.result.accessToken);
      localStorage.setItem('user', JSON.stringify(data.result.user));

      return data.result;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

export const register = createAsyncThunk<AuthResponse, RegisterData>(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
      });

      if (response.status === 429) {
        return rejectWithValue('Too many registration attempts. Please wait a moment and try again.');
      }

      const data = await readApiResponse(response);

      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.[0] || 'Registration failed');
      }

      // Store token and user in localStorage
      localStorage.setItem('accessToken', data.result.accessToken);
      localStorage.setItem('user', JSON.stringify(data.result.user));

      return data.result;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      state.user = null;
      state.accessToken = null;
      state.isAuthenticated = false;
      state.error = null;
    },
    /**
     * The session ended on its own (token expired or the API rejected it).
     * Dispatched by sessionMiddleware. Distinct from `logout`, which is a
     * deliberate user action, so cart/order slices can reset on either while
     * the UI can still explain *why* the user was signed out.
     */
    sessionExpired: (state) => {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      state.user = null;
      state.accessToken = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = SESSION_EXPIRED_MESSAGE;
    },
    clearError: (state) => {
      state.error = null;
    },
    setCredentials: (state, action: PayloadAction<{ user: User; accessToken: string }>) => {
      state.user = action.payload.user;
      state.accessToken = action.payload.accessToken;
      state.isAuthenticated = true;
      localStorage.setItem('accessToken', action.payload.accessToken);
      localStorage.setItem('user', JSON.stringify(action.payload.user));
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.accessToken = action.payload.accessToken;
        state.error = null;
        // Ensure token is saved to localStorage
        localStorage.setItem('accessToken', action.payload.accessToken);
        localStorage.setItem('user', JSON.stringify(action.payload.user));
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Register
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.accessToken = action.payload.accessToken;
        state.error = null;
        // Ensure token is saved to localStorage
        localStorage.setItem('accessToken', action.payload.accessToken);
        localStorage.setItem('user', JSON.stringify(action.payload.user));
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { logout, sessionExpired, clearError, setCredentials } = authSlice.actions;
export default authSlice.reducer;
